import React, { useCallback } from 'react';

import { Navigate, Link as ReactRouterLink, Route, Routes, useParams } from 'react-router-dom';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Thumbnail from '@amzn/meridian/thumbnail';
import { useQuery } from '@tanstack/react-query';
import Breadcrumbs from 'src/components/shared/breadcrumbs/breadcrumbs';
import CopyableText from 'src/components/shared/copyableText/copyableText';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { BADGE_PHOTO_THUMBNAIL_URL } from 'src/config/ExternalLinks';
import { getAssociateProfileQueryOptions } from 'src/services/associateProfile/associateProfileQueries';
import { useAppSelector } from 'src/store/store';

import AssociateProfileEmployeeInfo from './associateProfileEmployeeInfo';
import EmDash from './emdash';
import ModifiedDuty from './modifiedDuty';

import AssociateProfileHistory from './history/associateProfileHistory';
import AssociateProfilePlacements from './placements/associateProfilePlacements';
import AssociateProfileRestrictions from './restrictions/associateProfileRestrictions';

type Tab = 'placements' | 'restrictions' | 'history';

const AssociatesProfile = () => {
  const { alias, '*': restOfPath } = useParams();
  const { featureFlags } = useAppSelector((state) => state.user);
  const { selectedSite } = useAppSelector((state) => state.sites);

  const isModifiedDutyEnabled = featureFlags.NEW_MODIFIED_DUTY_EXPERIENCE && !!alias;

  const profileQuery = useQuery(getAssociateProfileQueryOptions.profile(alias ?? '', selectedSite.id));
  const modifiedDutyQuery = useQuery({
    ...getAssociateProfileQueryOptions.modifiedDutySummary(alias ?? ''),
    enabled: isModifiedDutyEnabled,
  });

  // Get current tab from URL
  const currentTab = restOfPath?.split('/')[0] || 'placements';

  const openNewJmrInNewTab = useCallback(() => {
    if (!profileQuery.data) {
      return;
    }

    window.open(`${window.location.origin}/newJmr/${profileQuery.data.alias}`, '_blank', 'noopener,noreferrer');
  }, [profileQuery.data]);

  if (profileQuery.isPending || (isModifiedDutyEnabled && modifiedDutyQuery.isPending)) {
    return <LoadingSpinner />;
  }

  if (profileQuery.isError) {
    return <Text>Oops! Something went wrong.</Text>;
  }

  const associateProfile = profileQuery.data;
  const modifiedDutySummary = modifiedDutyQuery.data ?? null;

  return (
    <Column spacing="500" spacingInset="450 500" overflowX="hidden" minWidth="750px">
      <Breadcrumbs
        breadcrumbList={[
          { url: '/placementsOverview', label: 'Placements Overview' },
          { url: `/associateProfile/${associateProfile?.alias}`, label: `Associate ${associateProfile?.name}` },
        ]}
      />
      <Column spacing="400">
        <Row alignmentVertical="center" widths={['fit', 'fill', 'fit']}>
          <Thumbnail source={BADGE_PHOTO_THUMBNAIL_URL + associateProfile?.alias} />
          <Box>
            <Text type="h400">{associateProfile?.name || <EmDash />}</Text>
            <Text type="b300">
              {associateProfile?.jobTitle || <EmDash />} &bull; &nbsp;
              {associateProfile?.alias ? (
                <Link
                  type="secondary"
                  href={`https://fclm-portal.amazon.com/search?term=${associateProfile?.alias}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >{`${associateProfile?.alias}@`}</Link>
              ) : (
                <EmDash />
              )}
            </Text>
          </Box>
          <Button onClick={openNewJmrInNewTab}>Create Placement</Button>
        </Row>
        <Row spacing="500" alignmentVertical="top">
          <AssociateProfileEmployeeInfo label="Employee ID">
            <Text>{associateProfile?.employeeId || <EmDash />}</Text>
          </AssociateProfileEmployeeInfo>
          <AssociateProfileEmployeeInfo label="Person ID">
            {associateProfile?.personId ? (
              <CopyableText value={associateProfile?.personId} ariaLabel="Copy person id" truncate />
            ) : (
              <EmDash />
            )}
          </AssociateProfileEmployeeInfo>
          <AssociateProfileEmployeeInfo label="Manager">
            {associateProfile?.managersAlias ? (
              <Link
                type="secondary"
                href={`https://phonetool.amazon.com/users/${associateProfile?.managersAlias}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {associateProfile?.managersName}
              </Link>
            ) : (
              <EmDash />
            )}
          </AssociateProfileEmployeeInfo>
          <AssociateProfileEmployeeInfo label="Home Location">
            <Text>{associateProfile?.location || <EmDash />}</Text>
          </AssociateProfileEmployeeInfo>
          <AssociateProfileEmployeeInfo label="Shift">
            <Text>{associateProfile?.shift || <EmDash />}</Text>
          </AssociateProfileEmployeeInfo>
          {(featureFlags.NEW_MODIFIED_DUTY_EXPERIENCE ||
            selectedSite.countryId === COUNTRY_ID_MAPPING.USA_COUNTRY_ID) && (
            <AssociateProfileEmployeeInfo label="Modified Duty">
              <ModifiedDuty
                totalAccommodatedDays={associateProfile?.totalAccommodatedDays}
                summaryResponse={modifiedDutySummary}
              />
            </AssociateProfileEmployeeInfo>
          )}
        </Row>
      </Column>
      <TabGroup value={currentTab} onChange={() => {}}>
        <ReactRouterLink to="placements">
          <Tab value="placements" aria-controls="tabpanel-placements" id="tab-placements">
            Placements
          </Tab>
        </ReactRouterLink>
        <ReactRouterLink to="restrictions">
          <Tab value="restrictions" aria-controls="tabpanel-restrictions" id="tab-restrictions">
            Restrictions
          </Tab>
        </ReactRouterLink>
        <ReactRouterLink to="history">
          <Tab value="history" aria-controls="tabpanel-history" id="tab-history">
            History
          </Tab>
        </ReactRouterLink>
      </TabGroup>
      <Routes>
        <Route index element={<Navigate to="placements" replace />} />
        <Route path="placements" element={<AssociateProfilePlacements />} />
        <Route path="restrictions" element={<AssociateProfileRestrictions />} />
        <Route path="history" element={<AssociateProfileHistory />} />
        <Route path="*" element={<Navigate to="placements" replace />} />
      </Routes>
    </Column>
  );
};

export default AssociatesProfile;
